// AdminWidgetsSpec.integrated-renders.js — 통합 메뉴 위젯 본문 render 함수 (2026-06-18 분리)
//
// AdminWidgetsSpec.integrated.js 가 300줄 초과 → render 함수만 분리 (기존 staff-renders 패턴).
// feature_type 별 본문 렌더만 담당. spec 빌더/사이즈/아이콘은 integrated.js 가 유지.
//
// 데이터 응답 형식 (admin-widgets-data.js 의 runIntegratedWidget):
//   { menuId, menuName, featureType, companies, lines: [{ companyCode, payload }] }

import React from 'react';
import { AttendanceIntegratedBody } from './AttendanceIntegratedBody';

// 회사별 라인 wrapper
function CompanyLines({ children }) {
  return (
    <span className="w-full flex flex-col items-end gap-0.5">{children}</span>
  );
}

// feature_type 미상 시 기본 render — '-'
export function renderUnknown() {
  return <span className="text-[10px] text-[var(--text-muted)]">-</span>;
}

// 갱신 통합 위젯 본문 — 회사별 라벨 + 각 갱신 건 "M/D 이름" 표시 (사용자 명세 2026-06-10).
//   지남 = 빨강, 예정 = indigo. 각 회사 위에 [CC] 라벨 + 그 아래 list.
export function renderRenewalIntegrated(d) {
  if (!d) return '-';
  const lines = Array.isArray(d.lines) ? d.lines : [];
  if (lines.length === 0) {
    return <span className="text-[10px] text-[var(--text-muted)]">없음</span>;
  }
  const fmtDate = (ymd) => {
    if (!ymd) return '';
    const [, m, dd] = String(ymd).split('-');
    return `${Number(m)}/${Number(dd)}`;
  };
  return (
    <span className="w-full flex flex-col items-end gap-1">
      {lines.map((line, i) => {
        const cc = String(line.companyCode || '').toUpperCase();
        const p = line.payload || {};
        const upcoming = Array.isArray(p.upcoming) ? p.upcoming : [];
        const overdue = Array.isArray(p.overdue) ? p.overdue : [];
        if (upcoming.length === 0 && overdue.length === 0) {
          return (
            <span key={`r-${cc}-${i}`} className="text-[9px] text-[var(--text-muted)]">
              [{cc}] 없음
            </span>
          );
        }
        return (
          <span key={`r-${cc}-${i}`} className="w-full flex flex-col items-end gap-0">
            <span className="text-[9px] font-bold text-[var(--text-sub)]">[{cc}]</span>
            {overdue.map((it, j) => (
              <span key={`r-${cc}-o-${j}`} className="text-[9px] font-bold text-amber-700 dark:text-amber-300 tabular-nums whitespace-nowrap">
                {fmtDate(it.date)} {it.name} <span className="text-[8px]">(지남)</span>
              </span>
            ))}
            {upcoming.map((it, j) => (
              <span key={`r-${cc}-u-${j}`} className="text-[9px] font-bold text-indigo-700 dark:text-indigo-300 tabular-nums whitespace-nowrap">
                {fmtDate(it.date)} {it.name}
              </span>
            ))}
          </span>
        );
      })}
    </span>
  );
}

// 1:1 통합 위젯 본문 — 회사별 라벨 + 미배정 상담자 이름 list (사용자 명세 2026-06-10).
//   카운트 X, 이름만. 0명이면 "없음" 표시.
export function renderConsultantIntegrated(d) {
  if (!d) return '-';
  const lines = Array.isArray(d.lines) ? d.lines : [];
  if (lines.length === 0) {
    return <span className="text-[10px] text-[var(--text-muted)]">없음</span>;
  }
  return (
    <span className="w-full flex flex-col items-end gap-1">
      {lines.map((line, i) => {
        const cc = String(line.companyCode || '').toUpperCase();
        const p = line.payload || {};
        const names = Array.isArray(p.unassignedConsultants) ? p.unassignedConsultants : [];
        if (names.length === 0) {
          return (
            <span key={`c-${cc}-${i}`} className="text-[9px] text-[var(--text-muted)]">
              [{cc}] 없음
            </span>
          );
        }
        return (
          <span key={`c-${cc}-${i}`} className="w-full flex flex-col items-end gap-0">
            <span className="text-[9px] font-bold text-[var(--text-sub)]">[{cc}]</span>
            {names.map((n, j) => (
              <span key={`c-${cc}-n-${j}`} className="text-[9px] font-bold text-violet-700 dark:text-violet-300 whitespace-nowrap">
                {n.name}
              </span>
            ))}
          </span>
        );
      })}
    </span>
  );
}

// 연차 통합 위젯 본문 — 회사별 오늘 연차 이름 list + 예정 7일 (날짜+이름) list.
//   사용자 명세 2026-06-10: 연차 현황 위젯처럼 표시, 두 회사 한 위젯.
export function renderLeaveIntegrated(d) {
  if (!d) return '-';
  const lines = Array.isArray(d.lines) ? d.lines : [];
  if (lines.length === 0) {
    return <span className="text-[10px] text-[var(--text-muted)]">없음</span>;
  }
  const fmtDate = (ymd) => {
    if (!ymd) return '';
    const [, m, dd] = String(ymd).split('-');
    return `${Number(m)}/${Number(dd)}`;
  };
  return (
    <span className="w-full flex flex-col items-end gap-1">
      {lines.map((line, i) => {
        const cc = String(line.companyCode || '').toUpperCase();
        const p = line.payload || {};
        const todayList = Array.isArray(p.today) ? p.today : [];
        const upcoming = Array.isArray(p.upcoming) ? p.upcoming : [];
        if (todayList.length === 0 && upcoming.length === 0) {
          return (
            <span key={`l-${cc}-${i}`} className="text-[9px] text-[var(--text-muted)]">
              [{cc}] 없음
            </span>
          );
        }
        return (
          <span key={`l-${cc}-${i}`} className="w-full flex flex-col items-end gap-0">
            <span className="text-[9px] font-bold text-[var(--text-sub)]">[{cc}]</span>
            {todayList.map((name, j) => (
              <span key={`l-${cc}-t-${j}`} className="text-[9px] font-bold text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                오늘 {name}
              </span>
            ))}
            {upcoming.map((it, j) => (
              <span key={`l-${cc}-u-${j}`} className="text-[9px] text-[var(--text-sub)] whitespace-nowrap">
                {fmtDate(it.date)} {it.name}
              </span>
            ))}
          </span>
        );
      })}
    </span>
  );
}

// 출퇴근 통합 위젯 본문 — 회사별로 출퇴근 통계 위젯과 동일 UI + 숫자 drill-down 모달.
//   상태(모달) 를 가지는 컴포넌트라 AttendanceIntegratedBody 로 분리.
export function renderAttendanceIntegrated(d) {
  if (!d) return '-';
  return <AttendanceIntegratedBody data={d} />;
}

// 출퇴근 달력 통합 위젯 본문 — "준비 중" (사용자 명세: 비즈니스 데이터 미정)
export function renderCalendarIntegrated(d) {
  if (!d) return '-';
  const lines = Array.isArray(d.lines) ? d.lines : [];
  if (lines.length === 0) {
    return <span className="text-[10px] text-[var(--text-muted)]">준비 중</span>;
  }
  return (
    <CompanyLines>
      {lines.map((line, i) => {
        const cc = String(line.companyCode || '').toUpperCase();
        return (
          <span key={`cal-${cc}-${i}`} className="inline-flex items-baseline gap-1 tabular-nums whitespace-nowrap">
            <span className="text-[10px] font-bold text-[var(--text-sub)]">[{cc}]</span>
            <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">준비 중</span>
          </span>
        );
      })}
    </CompanyLines>
  );
}
